import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, Loader2, FileDown, RefreshCw, PlayCircle, Send,
  CheckCircle, XCircle, Clock,
  View,
  Medal
} from 'lucide-react';
import { certificatesApi, type CertBatchRow, type CertTraineeRow, type CertificateEligibility, type EligibilityCriterion } from '../api';
import { useAuth } from '../hooks/useAuth';

// -------- Types (match the backend responses) --------
type BatchStatus = CertBatchRow['status'];
type GenStatus = CertTraineeRow['cert']['gen_status'];

interface BatchDrilldown {
  batch: {
    id: number;
    code: string;
    program: string;
    program_code: string;
    center: string;
    mp: string;
    trainer: string;
    status: BatchStatus;
    can_generate: boolean;
    can_publish: boolean;
  };
  trainees: CertTraineeRow[];
}

type View = 'list' | 'batch';

// -------- UI Helpers --------
function statusPill(s: GenStatus) {
  switch (s) {
    case 'GENERATED': return 'bg-green-100 text-green-800';
    case 'FAILED': return 'bg-red-100 text-red-800';
    case 'QUEUED': return 'bg-amber-100 text-amber-800';
    case 'GENERATING': return 'bg-blue-100 text-blue-800';
    case 'NOT_STARTED': default: return 'bg-gray-100 text-gray-700';
  }
}

function pct(n: number, d: number) {
  return d ? Math.round((n / d) * 100) : 0;
}

function fmtDateTime(s?: string | null) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

  const formatDateForInput = (d?: string | Date | null) => {
    if (!d) return "";
    const date = typeof d === "string" ? new Date(d) : d;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function EligibilityDetails({ eligibility }: { eligibility: CertificateEligibility }) {
  const failingReasons: string[] = [];
  if (!eligibility.criteria.attendance.passed) {
    failingReasons.push(`Attendance ${formatCriterionValue(eligibility.criteria.attendance)} (needs ${eligibility.criteria.attendance.threshold}%)`);
  }
  if (!eligibility.criteria.post.passed) {
    failingReasons.push(`Post Assessment ${formatCriterionValue(eligibility.criteria.post)} (needs ${eligibility.criteria.post.threshold}+)`);
  }
  if (!eligibility.criteria.ojt.passed) {
    failingReasons.push(`OJT Evaluation ${formatCriterionValue(eligibility.criteria.ojt)} (needs ${eligibility.criteria.ojt.threshold}%)`);
  }
  if (!eligibility.criteria.weighted.passed) {
    failingReasons.push(`Weighted score ${formatCriterionValue(eligibility.criteria.weighted)} (needs ${eligibility.criteria.weighted.threshold}%)`);
  }

  return (
    <div className="text-xs">
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
          eligibility.eligible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'
        }`}
      >
        {eligibility.eligible ? 'Eligible' : 'Not eligible'}
      </span>
      {!eligibility.eligible && (
        <ul className="mt-2 space-y-1 text-red-600">
          {failingReasons.length
            ? failingReasons.map((reason) => (
                <li key={reason} className="flex items-start gap-1">
                  <XCircle size={12} className="mt-0.5" />
                  <span>{reason}</span>
                </li>
              ))
            : eligibility.reason && (
                <li className="flex items-start gap-1">
                  <XCircle size={12} className="mt-0.5" />
                  <span>{eligibility.reason}</span>
                </li>
              )}
        </ul>
      )}
      {eligibility.eligible && (
        <p className="mt-1 text-[11px] text-green-700">
          Highest priority checks satisfied.
        </p>
      )}
    </div>
  );
}

function formatCriterionValue(criterion: EligibilityCriterion): string {
  if (criterion.raw?.value != null && criterion.raw?.max) {
    const percentText =
      criterion.value != null && criterion.unit
        ? ` (${criterion.value}${criterion.unit})`
        : criterion.value != null
          ? ` (${criterion.value})`
          : '';
    return `${criterion.raw.value}/${criterion.raw.max}${percentText}`;
  }
  if (criterion.value == null) return '—';
  if (criterion.unit === '%') return `${criterion.value}%`;
  if (criterion.max) return `${criterion.value}/${criterion.max}`;
  return criterion.unit ? `${criterion.value} ${criterion.unit}` : `${criterion.value}`;
}

// ----------------------------------------------------

export default function Certificates({ isAdmin = false }: { isAdmin?: boolean }) {

  const { user, isAuthenticated } = useAuth();
  const [view, setView] = useState<View>('list');
  isAdmin = user?.role === 'SYSTEM_ADMIN' ? true : false;
  // ---- List state
  const [rows, setRows] = useState<CertBatchRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<'' | BatchStatus>(''); // filter

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // ---- Drilldown state
  const [batch, setBatch] = useState<BatchDrilldown | null>(null);
  const [tSearch, setTSearch] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    if (!batch) {
      setSelected(new Set());
      return;
    }
    setSelected((prev) => {
      const allowed = new Set(
        batch.trainees
          .filter((t) => t.eligibility?.eligible)
          .map((t) => t.trainee_id)
      );
      let changed = false;
      const next = new Set<number>();
      prev.forEach((id) => {
        if (allowed.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [batch]);

  const loadList = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      isAdmin = user?.role === 'SYSTEM_ADMIN' ? true : false;
      const { data } = await certificatesApi.listBatches({ status: status || undefined, q: q || undefined, page, pageSize });
      if (data?.success && data.data) {
        setRows(data.data.rows);
        setTotal(data.data.total);
      }
    } catch (e) {
      setErr('Failed to load batches.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [q, status, page, user]);

  useEffect(() => { if (view === 'list') { loadList(); } }, [loadList, view]);

  const openBatch = useCallback(async (b: CertBatchRow) => {
    setLoading(true); setErr(null);
    try {
      const { data } = await certificatesApi.getBatch(b.batch_id);
      if (data?.success && data.data) {
        setBatch(data.data);
        setSelected(new Set());
        setView('batch');
      }
    } catch (e) {
      setErr('Failed to load batch.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const backToList = useCallback(() => {
    setBatch(null);
    setView('list');
    setSelected(new Set());
    // stop polling
    if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  // Poll while any trainee is QUEUED/GENERATING
  useEffect(() => {
    if (view !== 'batch' || !batch) return;
    const hasPending = batch.trainees.some(t => t.cert.gen_status === 'QUEUED' || t.cert.gen_status === 'GENERATING');
    if (!hasPending) {
      if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return; // already polling
    pollRef.current = window.setInterval(async () => {
      try {
        const { data } = await certificatesApi.getBatch(batch.batch.id);
        if (data?.success && data.data) setBatch(data.data);
      } catch (e) { console.error(e); }
    }, 5000) as unknown as number;
    return () => { if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; } };
  }, [view, batch]);

  // ---- Derived stats for drilldown
  const filteredTrainees = useMemo(() => {
    if (!batch) return [];
    const term = tSearch.toLowerCase().trim();
    return term
      ? batch.trainees.filter(t => t.name.toLowerCase().includes(term) || String(t.trainee_id).includes(term))
      : batch.trainees;
  }, [batch, tSearch]);
  const selectableVisible = useMemo(
    () => filteredTrainees.filter(t => t.eligibility?.eligible),
    [filteredTrainees]
  );

  const counts = useMemo(() => {
    if (!batch) return { total: 0, generated: 0, failed: 0, published: 0, queued: 0, generating: 0 };
    const total = batch.trainees.length;
    let generated = 0, failed = 0, published = 0, queued = 0, generating = 0;
    for (const t of batch.trainees) {
      if (t.cert.is_published) published++;
      switch (t.cert.gen_status) {
        case 'GENERATED': generated++; break;
        case 'FAILED': failed++; break;
        case 'QUEUED': queued++; break;
        case 'GENERATING': generating++; break;
      }
    }
    return { total, generated, failed, published, queued, generating };
  }, [batch]);
  const hasEligible = batch?.trainees.some(t => t.eligibility?.eligible) ?? false;
  const eligibilityRules = useMemo(() => {
    const defaults = [
      'Attendance ≥ 70% on recorded sessions (launchpad excluded)',
      'Post-assessment score ≥ 50/100',
      'OJT evaluation score ≥ 50% (≥25/50)',
      'Weighted score (80% Post + 20% OJT) ≥ 55%',
    ];
    const criteria = batch?.trainees?.[0]?.eligibility?.criteria;
    if (!criteria) return defaults;
    const ojtRawMax = criteria.ojt.raw?.max ?? 50;
    const ojtRawMin = ojtRawMax ? Math.round((criteria.ojt.threshold / 100) * ojtRawMax) : 0;
    return [
      `Attendance ≥ ${criteria.attendance.threshold}% on recorded sessions (launchpad excluded)`,
      `Post-assessment score ≥ ${criteria.post.threshold}/${criteria.post.max ?? 100}`,
      `OJT evaluation score ≥ ${criteria.ojt.threshold}% (≥${ojtRawMin}/${ojtRawMax})`,
      `Weighted score ≥ ${criteria.weighted.threshold}% (${criteria.weighted.note ?? '80% Post + 20% OJT'})`,
    ];
  }, [batch]);

  // ---- Actions
  const toggle = (trainee: CertTraineeRow) => {
    if (!trainee.eligibility?.eligible) return;
    setSelected(prev => {
      const n = new Set(prev);
      n.has(trainee.trainee_id) ? n.delete(trainee.trainee_id) : n.add(trainee.trainee_id);
      return n;
    });
  };
  const toggleAllVisible = () => {
    if (!selectableVisible.length) return;
    const allSelected = selectableVisible.every(t => selected.has(t.trainee_id));
    const next = new Set(selected);
    if (allSelected) {
      selectableVisible.forEach(t => next.delete(t.trainee_id));
    } else {
      selectableVisible.forEach(t => next.add(t.trainee_id));
    }
    setSelected(next);
  };

  const queueGenerate = async () => {
    if (!batch) return;
    const eligibleIds = batch.trainees
      .filter(t => t.eligibility?.eligible)
      .map(t => t.trainee_id);
    const ids = selected.size ? Array.from(selected) : eligibleIds;
    if (!ids.length) {
      alert('No eligible trainees available for certificate generation.');
      return;
    }
    try {
      await certificatesApi.generate(batch.batch.id, ids);
      // refresh immediately
      const { data } = await certificatesApi.getBatch(batch.batch.id);
      if (data?.success && data.data) setBatch(data.data);
      setSelected(new Set());
    } catch (e) {
      console.error(e);
      const serverMessage = (e as any)?.response?.data?.message;
      alert(serverMessage || 'Failed to queue generation.');
    }
  };

  const publishSelected = async () => {
    if (!batch) return;
    try {
      const ids = Array.from(selected);
      await certificatesApi.publish(batch.batch.id, ids.length ? ids : undefined);
      const { data } = await certificatesApi.getBatch(batch.batch.id);
      if (data?.success && data.data) setBatch(data.data);
      await loadList(); // update list counts/status
    } catch (e) {
      console.error(e);
      alert('Failed to publish.');
    }
  };

  const exportCsv = async (b: { id: number; code: string }) => {
    try {
      const res = await certificatesApi.exportCsv(b.id);
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      downloadBlob(`certificates_${b.code}.csv`, blob);
    } catch (e) {
      console.error(e);
      alert('Export failed.');
    }
  };

  const regenerateOne = async (row: CertTraineeRow) => {
    if (!row.cert.certificate_id) return;
    try {
      await certificatesApi.regenerateOne(row.cert.certificate_id);
      const { data } = await certificatesApi.getBatch(batch!.batch.id);
      if (data?.success && data.data) setBatch(data.data);
    } catch (e) {
      console.error(e);
      alert('Failed to re-queue generation.');
    }
  };

  const downloadOne = async (row: CertTraineeRow) => {
    if (!row.cert.certificate_id) return;
    try {
      const { data } = await certificatesApi.donwoadlOneCert(row.cert.certificate_id);
      if (data?.success && data.data) {
        const url = data?.data?.url;
        if (!url) return;
        // const absoluteUrl = url.startsWith('http')
        //   ? url
        //   : `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}${url}`;

        window.open(url, '_blank', 'noopener');
      }
    } catch (e) {
      console.error(e);
      alert('Download failed.');
    }
  };


  // ---------- Renders ----------
  const renderList = () => (
    <>
      <header className="mb-6 md:mb-8">
        <h1 className="text-3xl font-extrabold text-gray-900">Certificates</h1>
        <p className="mt-1 text-base text-gray-500">Generate and publish certificates per batch.</p>
      </header>

      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 mb-6 md:mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search size={18} className="absolute left-3 top-2.5 text-gray-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Batch code or program name"
                className="pl-9 pr-3 py-2 rounded-lg border-gray-300 w-full focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as any)}
              className="block w-full rounded-lg border-gray-300 py-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">All</option>
              <option value="InProgress">InProgress</option>
              <option value="InPlacement">InPlacement</option>
              <option value="Completed">Completed</option>
              {/* <option value="Live">Live</option>
              <option value="Draft">Draft</option>
              <option value="Cancelled">Cancelled</option> */}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-gray-500">
          <Loader2 className="animate-spin h-8 w-8 mx-auto text-indigo-500" />
          <p className="mt-3">Loading batches…</p>
        </div>
      ) : err ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4">{err}</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center text-gray-500">
          No batches found.
        </div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-2xl shadow-sm">
          <table className="w-full whitespace-nowrap divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Batch</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Program</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600">Progress</th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map(r => {
                const prog = pct(r.totals.generated + r.totals.failed, r.totals.enrolled);
                return (
                  <tr key={r.batch_id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{r.batch_code}</div>
                      <div className="text-xs text-gray-500">Center: {r.center || '—'} • MP: {r.mp || '—'}</div>
                      <div className="text-xs text-gray-500">Trainer: {r.trainer || '—'}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{r.program} ({r.program_code})</td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">{r.status}</span>
                      {r.eligible_to_generate && <span className="ml-2 text-[11px] text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Eligible</span>}
                    </td>
                   <td className="px-6 py-5">
                      <div className="flex items-center space-x-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2.5">
                          <div className="bg-indigo-600 h-2.5 rounded-full" style={{ width: `${prog}%` }} />
                        </div>
                        <div className="text-xs text-gray-600 font-medium">
                          {r.totals.generated + r.totals.failed}/{r.totals.enrolled}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                          Generated:{r.totals.generated} • Failed:{r.totals.failed} • Published:{r.totals.published}
                        </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex items-center space-x-3">
                        <button className="text-indigo-600 hover:text-indigo-900" onClick={() => openBatch(r)}>Open</button>
                        {/* <span className="text-gray-300">|</span>
                        <button
                          className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"
                          onClick={async () => {
                            try {
                              const res = await certificatesApi.exportCsv(r.batch_id);
                              const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
                              downloadBlob(`certificates_${r.batch_code}.csv`, blob);
                            } catch (e) { console.error(e); alert('Export failed.'); }
                          }}
                        >
                          <FileDown size={16} className="mr-1" /> Export
                        </button> */}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* simple pagination */}
          {Math.ceil(total / pageSize) > 1 && (
            <div className="flex justify-between items-center p-4">
              <span className="text-sm text-gray-600">Page {page} of {Math.ceil(total / pageSize)}</span>
              <div className="space-x-2">
                <button
                  className="px-3 py-1 rounded border text-sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <button
                  className="px-3 py-1 rounded border text-sm"
                  disabled={page * pageSize >= total}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );

  const renderBatch = () => {
    if (!batch) return null;

  const allVisibleSelected =
    selectableVisible.length > 0 && selectableVisible.every(t => selected.has(t.trainee_id));

    return (
      <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
        <div className="flex items-center mb-4">
          <button onClick={backToList} className="text-gray-600 hover:text-gray-900 mr-3">← Back</button>
          <h2 className="text-2xl font-bold text-gray-900">Batch {batch.batch.code}</h2>
        </div>
        <div className="text-sm text-gray-600 mb-3">
          Program: <span className="font-medium">{batch.batch.program} ({batch.batch.program_code})</span> • Center: {batch.batch.center || '—'} • MP: {batch.batch.mp || '—'} • Trainer: {batch.batch.trainer || '—'}
        </div>

        {/* Progress summary */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
          <SummaryCard icon={<CheckCircle className="text-green-600" size={16} />} label="Generated" value={counts.generated} />
          <SummaryCard icon={<XCircle className="text-red-600" size={16} />} label="Failed" value={counts.failed} />
          <SummaryCard icon={<Send className="text-indigo-600" size={16} />} label="Published" value={counts.published} />
          <SummaryCard icon={<Clock className="text-amber-600" size={16} />} label="Queued" value={counts.queued} />
          <SummaryCard icon={<Loader2 className="text-blue-600" size={16} />} label="Generating" value={counts.generating} />
          {/* <SummaryCard icon={<Download className="text-gray-700" size={16} />} label="Total" value={counts.total} /> */}
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
            <input
              value={tSearch}
              onChange={(e) => setTSearch(e.target.value)}
              placeholder="Search trainee by name or ID"
              className="pl-9 pr-3 py-2 rounded-lg border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="flex-1" />

          <button
            className="inline-flex items-center px-3 py-2 rounded-lg border text-sm hover:bg-gray-50"
            onClick={() => exportCsv(batch.batch)}
          >
            <FileDown size={16} className="mr-1" /> Export CSV
          </button>

          {isAdmin &&(
            <>
              <button
                className={`inline-flex items-center px-3 py-2 rounded-lg text-sm text-white ${
                  batch.batch.can_generate && hasEligible
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
                onClick={queueGenerate}
                disabled={!batch.batch.can_generate || !hasEligible}
                title={
                  !hasEligible
                    ? 'No trainees meet the eligibility criteria yet'
                    : batch.batch.can_generate
                      ? 'Queue generate for selected (or all eligible if none selected)'
                      : 'Not eligible yet (Can only do once Post assessment is ended)'
                }
              >
                <PlayCircle size={16} className="mr-1" /> Queue Generate
              </button>

              <button
                className={`inline-flex items-center px-3 py-2 rounded-lg text-sm text-white ${batch.batch.can_publish ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-400 cursor-not-allowed'}`}
                onClick={publishSelected}
                disabled={!batch.batch.can_publish}
                title={batch.batch.can_publish ? 'Publish selected (or all generated if none selected)' : 'Not eligible yet(Need one generated atleast)'}
              >
                <Send size={16} className="mr-1" /> Publish
              </button>
            </>
          )}
        </div>

        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
          <p className="text-sm font-semibold text-amber-900">Certificate eligibility checklist</p>
          <ul className="mt-2 list-disc space-y-1 pl-4">
            {eligibilityRules.map(rule => (
              <li key={rule}>{rule}</li>
            ))}
          </ul>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    disabled={!selectableVisible.length}
                    onChange={toggleAllVisible}
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Trainee</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Eligibility</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Published</th>
                {/* <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Attempts</th> */}
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredTrainees.map(t => (
                <tr
                  key={t.trainee_id}
                  className={`hover:bg-gray-50 ${
                    t.eligibility?.eligible ? '' : 'opacity-60 bg-gray-50'
                  }`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      disabled={!t.eligibility?.eligible}
                      checked={t.eligibility?.eligible ? selected.has(t.trainee_id) : false}
                      onChange={() => toggle(t)}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">{t.name}</div>
                    <div className="text-xs text-gray-500">ID: {t.trainee_id} • {t.email || '—'}</div>
                    {t.cert.serial_no && (
                      <div className="text-[11px] text-gray-500 mt-0.5">Serial: {t.cert.serial_no}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    {t.eligibility ? (
                      <EligibilityDetails eligibility={t.eligibility} />
                    ) : (
                      <span className="text-xs text-gray-500">Eligibility pending</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${statusPill(t.cert.gen_status)}`}>
                      {t.cert.gen_status.replace(/_/g, ' ')}
                    </span>
                    {t.cert.gen_error && (
                      <div className="text-[11px] text-red-600 mt-1 max-w-xs truncate" title={t.cert.gen_error}>
                        {t.cert.gen_error}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {t.cert.is_published ? (
                      <span className="text-green-700 bg-green-100 rounded-full px-2 py-0.5 text-xs">
                        Yes • {formatDateForInput(t.cert.published_at)}
                      </span>
                    ) : (
                      <span className="text-gray-500 text-xs">No</span>
                    )}
                  </td>
                  {/* <td className="px-4 py-3 text-sm text-gray-700">{t.cert.gen_attempts}</td> */}
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center space-x-2">
                      {isAdmin && t.cert.gen_status === 'FAILED' && t.cert.certificate_id && (
                        <button
                          onClick={() => regenerateOne(t)}
                          className="inline-flex items-center px-2 py-1 text-xs rounded border hover:bg-gray-50"
                          title="Re-queue generate"
                        >
                          <RefreshCw size={14} className="mr-1" /> Retry
                        </button>
                      )}
                      {isAdmin && t.cert.gen_status === 'GENERATED' && t.cert.certificate_id && (
                        <button
                          onClick={() => downloadOne(t)}
                          className="inline-flex items-center px-2 py-1 text-xs rounded border hover:bg-gray-50"
                          title="View / Download"
                        >
                          <Medal size={14} className="mr-1" />
                        </button>
                      )}
                      {/* {isAdmin && t.cert.gen_status === 'GENERATED' && t.cert.certificate_id && (
                        <button
                          onClick={() => downloadOne(t)}
                          className="inline-flex items-center px-2 py-1 text-xs rounded border hover:bg-gray-50"
                          title="Download (admin)"
                        >
                          <Download size={14} className="mr-1" /> Download
                        </button>
                      )}
                      {isAdmin && t.cert.gen_status === 'GENERATED' && t.cert.certificate_id && (
                        <button
                          onClick={async () => {
                            try {
                              if(!t.cert.certificate_id) return;
                              const resp = await certificatesApi.viewOne(t.cert.certificate_id);
                              const url = resp.data?.data?.url;
                              if (url) window.open(url, '_blank', 'noopener');
                            } catch (e) {
                              console.error(e);
                              alert('Unable to open certificate.');
                            }
                          }}
                          className="inline-flex items-center px-3 py-1.5 rounded border text-sm hover:bg-gray-50"
                        >
                          View
                        </button>
                      )} */}
                    </div>
                  </td>
                </tr>
              ))}
              {filteredTrainees.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">No trainees found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-100 min-h-screen p-4 sm:p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        {view === 'list' ? renderList() : renderBatch()}
      </div>
    </div>
  );
}

// Small summary card
function SummaryCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number | string }) {
  return (
    <div className="bg-white border rounded-xl p-3 flex items-center space-x-2">
      <div>{icon}</div>
      <div>
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-sm font-semibold text-gray-900">{value}</div>
      </div>
    </div>
  );
}
